import { pickT } from "@repo/shared";
import { CheckCircle2, FileCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { T } from "./page.i18n";

/** Storage can throw (blocked cookies, private mode); the page must render and clear regardless. */
function safely(action: () => void) {
  try {
    action();
  } catch {
    // nothing to read or clear
  }
}

function safeSessionGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

export default function SignatureCompletePage() {
  // Read in the initialisers, which are pure: the effect below clears the keys, and under
  // StrictMode (dev) it runs twice, so reading there made the second run overwrite the
  // name with null.
  const [clientName] = useState<string | null>(() => safeSessionGet("signatureClientName"));
  const [lang] = useState<string>(() => safeSessionGet("signatureFlowLanguage") ?? "en_GB");

  useEffect(() => {
    // Clean up session after displaying
    safely(() => sessionStorage.removeItem("signatureClientName"));
    safely(() => sessionStorage.removeItem("signatureFlowLanguage"));

    // Clear public auth so next link visit requires re-authentication
    safely(() => localStorage.removeItem("publicSessionToken"));
    safely(() => localStorage.removeItem("publicSessionToken_for"));
  }, []);

  const t = pickT(T, lang);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-2xl w-full">
        <CardHeader className="text-center pb-4">
          <div className="mx-auto w-16 h-16 bg-success/20 rounded-full flex items-center justify-center mb-4">
            <CheckCircle2 className="w-10 h-10 text-success" />
          </div>
          <CardTitle className="text-3xl">{t.documentsSigned}</CardTitle>
          <CardDescription className="text-lg mt-2">{t.signatureRecorded}</CardDescription>
        </CardHeader>

        <CardContent className="text-center space-y-6">
          <div className="space-y-2">
            {clientName && (
              <p className="text-lg">
                {t.thankYou} <span className="font-semibold">{clientName}</span>!
              </p>
            )}
            <p className="text-muted-foreground">{t.documentsSecurelyStored}</p>
          </div>

          <div className="bg-muted rounded-lg p-6 space-y-3">
            <div className="flex items-center justify-center gap-2 text-success">
              <FileCheck className="w-5 h-5" />
              <p className="font-medium">{t.signatureEvidenceRecorded}</p>
            </div>
            <ul className="text-sm text-muted-foreground space-y-1 text-left max-w-md mx-auto">
              <li>✓ {t.dateAndTime}</li>
              <li>✓ {t.authMethodVerified}</li>
              <li>✓ {t.contactInfoConfirmed}</li>
              <li>✓ {t.digitalSignatureStored}</li>
            </ul>
          </div>

          <div className="bg-muted rounded-lg p-6 space-y-2">
            <p className="font-medium">{t.whatHappensNext}</p>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>✓ {t.advisorWillProcess}</li>
              <li>✓ {t.receiveCopies}</li>
              <li>✓ {t.legallyBinding}</li>
            </ul>
          </div>

          <p className="text-sm text-muted-foreground pt-4">{t.closeWindow}</p>
        </CardContent>
      </Card>
    </div>
  );
}
